import axios, { getData } from "../../../axios";
import * as tPromise from "io-ts-promise";
import { createErrorReportingAsyncThunk } from "../../helpers";
import {
  DriverActivityResponse,
  driverActivityResponseT,
  webSocketDriverLoginEventT,
  webSocketDriverStopDepartedEventT,
  webSocketDriverStopCompletedEventT,
  webSocketDriverStopArrivedEventT,
} from "dora-contracts";
import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../redux-store";

const prefix = "data/driver-activities";

type State = {
  // Routes we've already fetched activities for, so
  // loadDriversActivitiesForRouteIfNotLoaded(Many) don't refetch.
  loadedRouteIds: Record<string, true>;
  // Login status is also available (and preferred, once
  // "driver-login-from-route-view" is on) from the route view (route-views
  // duck) — kept here too since neither route_views.driverLogins nor
  // stopStatuses has a historic backfill.
  byRouteId: DriverActivityResponse["logins"];
  completedStops: Record<string, DriverActivityResponse["stopsCompleted"]["0"]>;
  arrivedStops: Record<string, DriverActivityResponse["stopsArrived"]["0"]>;
  departedStops: Record<string, DriverActivityResponse["stopsDeparted"]["0"]>;
};

const initialState: State = {
  loadedRouteIds: {},
  byRouteId: {},
  completedStops: {},
  arrivedStops: {},
  departedStops: {},
};

export const loadDriversActivitiesForRoute = createErrorReportingAsyncThunk(
  `${prefix}/load-drivers-activities-for-route`,
  async (routeId: string) => {
    return axios
      .get(`/api/driver-activities?routeId=${routeId}`)
      .then(getData)
      .then(tPromise.decode(driverActivityResponseT));
  }
);

export const loadDriversActivitiesForRoutes = createErrorReportingAsyncThunk(
  `${prefix}/load-drivers-activities-for-routes`,
  async (routeIds: string[]) => {
    return axios
      .post(`/api/driver-activities/get-for-routes`, { routeIds })
      .then(getData)
      .then(tPromise.decode(driverActivityResponseT));
  }
);

/**
 * Called when a web-socket message is received
 */
export const driverStopCompletedActivity = createErrorReportingAsyncThunk(
  `${prefix}/driver-stop-completed-activity`,
  async (event: unknown) => {
    return tPromise.decode(webSocketDriverStopCompletedEventT, event);
  }
);

/**
 * Called when a web-socket message is received
 */
export const driverStopArrivedActivity = createErrorReportingAsyncThunk(
  `${prefix}/driver-stop-arrived-activity`,
  async (event: unknown) => {
    return tPromise.decode(webSocketDriverStopArrivedEventT, event);
  }
);

/**
 * Called when a web-socket message is received
 */
export const driverStopDepartedActivity = createErrorReportingAsyncThunk(
  `${prefix}/driver-stop-departed-activity`,
  async (event: unknown) => {
    return tPromise.decode(webSocketDriverStopDepartedEventT, event);
  }
);

/**
 * Called when a web-socket message is received
 */
export const driverLoggedInActivity = createErrorReportingAsyncThunk(
  `${prefix}/driver-logged-in-activity`,
  async (event: unknown) => {
    return tPromise.decode(webSocketDriverLoginEventT, event);
  }
);

export const loadDriversActivitiesForRouteIfNotLoaded =
  createErrorReportingAsyncThunk(
    `${prefix}/load-drivers-activities-for-route-if-not-loaded`,
    async (routeId: string, { getState, dispatch }) => {
      const state = getState() as RootState;
      const loaded = state.data.driverActivities.loadedRouteIds[routeId];
      if (!loaded) {
        return dispatch(loadDriversActivitiesForRoute(routeId));
      }
    }
  );

export const loadDriversActivitiesForRouteIfNotLoadedMany =
  createErrorReportingAsyncThunk(
    `${prefix}/load-drivers-activities-for-route-if-not-loaded-many`,
    async (routeIds: string[], { getState, dispatch }) => {
      const state = getState() as RootState;
      const routeIdsFiltered = routeIds.filter(
        (id) => !state.data.driverActivities.loadedRouteIds[id]
      );
      if (!routeIdsFiltered.length) {
        return;
      }
      await dispatch(loadDriversActivitiesForRoutes(routeIdsFiltered));
    }
  );

// Stop statuses and driver logins come from the route view itself once both
// flags are on; the activity log is only needed while one of them is off.
// Dispatched by the listener in ./listeners.ts after a route/calendar view
// load, once features are loaded, so the flags read here are the real ones.
export const loadDriversActivitiesForViews = createErrorReportingAsyncThunk(
  `${prefix}/load-drivers-activities-for-views`,
  async (routeIds: string[], { getState, dispatch }) => {
    const { features } = (getState() as RootState).features;
    if (
      features["stop-status-from-route-view"]?.enabled &&
      features["driver-login-from-route-view"]?.enabled
    ) {
      return;
    }
    if (features["fetch-all-route-views-activities"]?.enabled) {
      await dispatch(loadDriversActivitiesForRouteIfNotLoadedMany(routeIds));
    } else {
      routeIds.forEach((routeId) => {
        dispatch(loadDriversActivitiesForRouteIfNotLoaded(routeId));
      });
    }
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadDriversActivitiesForRoutes.fulfilled, (state, action) => {
        for (const routeId of action.meta.arg) {
          state.loadedRouteIds[routeId] = true;
        }
        for (const [routeId, login] of Object.entries(action.payload.logins)) {
          state.byRouteId[routeId] = {
            driverId: login.driverId,
            truckId: login.truckId,
            trailerId: login.trailerId,
            loggedInAt: login.loggedInAt,
          };
        }
        for (const stop of action.payload.stopsCompleted) {
          state.completedStops[stop.stopId] = stop;
        }
        for (const stop of action.payload.stopsArrived) {
          state.arrivedStops[stop.stopId] = stop;
        }
        for (const stop of action.payload.stopsDeparted) {
          state.departedStops[stop.stopId] = stop;
        }
      })
      .addCase(loadDriversActivitiesForRoute.fulfilled, (state, action) => {
        state.loadedRouteIds[action.meta.arg] = true;
        if (action.payload.logins[action.meta.arg]) {
          state.byRouteId[action.meta.arg] = {
            driverId: action.payload.logins[action.meta.arg].driverId,
            truckId: action.payload.logins[action.meta.arg].truckId,
            trailerId: action.payload.logins[action.meta.arg].trailerId,
            loggedInAt: action.payload.logins[action.meta.arg].loggedInAt,
          };
        }
        for (const stop of action.payload.stopsCompleted) {
          state.completedStops[stop.stopId] = stop;
        }
        for (const stop of action.payload.stopsArrived) {
          state.arrivedStops[stop.stopId] = stop;
        }
        for (const stop of action.payload.stopsDeparted) {
          state.departedStops[stop.stopId] = stop;
        }
      })
      .addCase(driverLoggedInActivity.fulfilled, (state, action) => {
        const activity = action.payload;
        const current = state.byRouteId[activity.routeId] || {};
        if (!current.loggedInAt || current.loggedInAt < activity.loggedInAt) {
          state.byRouteId[activity.routeId] = {
            driverId: activity.driverId,
            truckId: activity.truckId,
            trailerId: activity.trailerId,
            loggedInAt: activity.loggedInAt,
          };
        }
      })
      .addCase(driverStopCompletedActivity.fulfilled, (state, action) => {
        state.completedStops[action.payload.stopId] = action.payload;
      })
      .addCase(driverStopArrivedActivity.fulfilled, (state, action) => {
        state.arrivedStops[action.payload.stopId] = action.payload;
      })
      .addCase(driverStopDepartedActivity.fulfilled, (state, action) => {
        state.departedStops[action.payload.stopId] = action.payload;
      });
  },
});

export default slice.reducer;
